import { useEffect, useRef, useState } from 'react';
import { Camera, X } from 'lucide-react';

/**
 * CameraCapture — ถ่ายรูป "สด" จากกล้องเท่านั้น (getUserMedia) กันเลือกรูปจากแกลเลอรี.
 *
 * ทำไมไม่ใช้ <input type="file" capture>: attribute `capture` เป็นแค่ "คำใบ้"
 * — Android/LINE webview เลือกไม่ทำตามได้ ทำให้ยังโผล่แกลเลอรี (อัปรูปเก่า/ปลอมได้).
 * กล้องสดผ่าน stream ไม่มีทางเข้าถึงแกลเลอรีเลย.
 *
 * Fallback: บาง iOS webview บล็อก getUserMedia → เรียก onUnsupported() ให้ผู้เรียก
 * กลับไปใช้ <input capture> เดิม (ดีกว่าเช็คอินไม่ได้เลย).
 */
interface Props {
  open: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
  onUnsupported: () => void;
  filenameHint?: string;
}

export function CameraCapture({ open, onClose, onCapture, onUnsupported, filenameHint }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [ready, setReady] = useState(false);

  // เก็บ callback ใน ref → effect ผูกแค่ `open` ไม่ re-init กล้องทุก re-render
  const onUnsupportedRef = useRef(onUnsupported);
  onUnsupportedRef.current = onUnsupported;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setReady(false);

    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        onUnsupportedRef.current();
        return;
      }
      // บังคับ "กล้องหน้า" (เซลฟี่) ให้เสถียร: บางเครื่อง/LINE webview ไม่ทำตาม
      // facingMode:'user' แบบ ideal แล้วไปเปิดกล้องหลัง → ใช้ exact ก่อน (บังคับจริง)
      // ถ้าเครื่องไม่มีกล้องหน้า (OverconstrainedError) ค่อย fallback เป็น ideal
      const openFrontCamera = async (): Promise<MediaStream> => {
        try {
          return await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { exact: 'user' } },
            audio: false,
          });
        } catch (e) {
          if (e instanceof DOMException && (e.name === 'OverconstrainedError' || e.name === 'NotFoundError')) {
            return navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
          }
          throw e;
        }
      };
      try {
        const stream = await openFrontCamera();
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setReady(true);
      } catch {
        // ปิดสิทธิ์ / webview ไม่รองรับ (iOS บางรุ่น) → fallback ไป input เดิม
        if (!cancelled) onUnsupportedRef.current();
      }
    })();

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [open]);

  function snap() {
    const video = videoRef.current;
    if (!video) return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // mirror แนวนอนให้ตรงกับพรีวิว (กล้องหน้า = ส่องกระจก) — ไม่งั้นรูปที่ได้กลับซ้าย-ขวา
    ctx.translate(w, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, w, h);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `${filenameHint ?? 'selfie'}_${Date.now()}.jpg`, {
          type: 'image/jpeg',
        });
        onCapture(file);
      },
      'image/jpeg',
      0.9,
    );
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      <div className="flex-1 relative flex items-center justify-center overflow-hidden">
        <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" style={{ transform: 'scaleX(-1)' }} />
        {!ready && (
          <div className="absolute inset-0 flex items-center justify-center text-white text-sm">
            กำลังเปิดกล้อง…
          </div>
        )}
      </div>
      <div
        className="bg-black flex items-center justify-between px-6 pt-4"
        style={{ paddingBottom: 'calc(1.25rem + env(safe-area-inset-bottom))' }}
      >
        <button type="button" onClick={onClose} className="text-white/80 p-3" aria-label="ยกเลิก">
          <X className="w-6 h-6" />
        </button>
        <button
          type="button"
          onClick={snap}
          disabled={!ready}
          aria-label="ถ่ายรูป"
          className="w-16 h-16 rounded-full bg-white ring-4 ring-white/40 disabled:opacity-40 flex items-center justify-center active:scale-95 transition"
        >
          <Camera className="w-7 h-7 text-black" />
        </button>
        <div className="w-12" />
      </div>
    </div>
  );
}
